import React, { useState, useEffect, useMemo } from 'react';
import { Modal, Spinner } from 'react-bootstrap';
import { BASEAPIURL, token } from '../../env';
import './OfferPage.css';


const OfferPage = () => {
  const [menuData, setMenuData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showError, setShowError] = useState(false);
  const [showModal, setShowModal] = useState({ bar: false, restaurant: false, health: false });
  const [showNoOffersMessage, setShowNoOffersMessage] = useState(false);

  // Validate item structure
  const isValidItem = (item) => {
    return (
      item &&
      typeof item === 'object' &&
      item.SchemeTitle &&
      typeof item.SchemeTitle === 'string' &&
      typeof item.SchemeDesc === 'string' &&
      typeof item.SchemeFromDate === 'string' &&
      typeof item.SchemeToDate === 'string' &&
      typeof item.SchemeRate !== 'undefined' &&
      typeof item.SchemeDisRate !== 'undefined' &&
      typeof item.SchemeQty !== 'undefined' &&
      typeof item.SchemeFreeQty !== 'undefined'
    );
  };

  // Check if offer is valid today (inclusive)
  const isOfferValidToday = (fromDateStr, toDateStr) => {
    if (!fromDateStr || !toDateStr) return false;

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const fromDate = new Date(fromDateStr);
    const toDate = new Date(toDateStr);

    fromDate.setHours(0, 0, 0, 0);
    toDate.setHours(0, 0, 0, 0);

    return today >= fromDate && today <= toDate;
  };

  // Custom date formatting
  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      const date = new Date(dateStr);
      const options = { year: 'numeric', month: 'short', day: '2-digit' };
      return date.toLocaleDateString('en-US', options);
    } catch {
      return dateStr;
    }
  };

  const formatCurrency = (amount) => {
    return amount != null ? `₹${Number(amount).toFixed(2)}` : 'N/A';
  };

  const formatDiscount = (discount) => {
    return discount != null ? `${Number(discount)}%` : 'N/A';
  };

  // Fetch menu data
  const fetchMenuData = async () => {
    if (!token) {
      setError('Authentication token is missing. Please log in.');
      setShowError(true);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    setShowError(false);
    try {
      const myHeaders = new Headers();
      myHeaders.append('Content-Type', 'application/json');
      myHeaders.append('Authorization', `Bearer ${token}`);

      const raw = JSON.stringify({ tokenData: token });

      const requestOptions = {
        method: 'POST',
        headers: myHeaders,
        body: raw,
        redirect: 'follow',
      };

      const response = await fetch(`${BASEAPIURL}/SchemeList`, requestOptions);

      const result = await response.json();

      let dataToUse = [];
      if (Array.isArray(result)) {
        dataToUse = result;
      } else if (result.data && Array.isArray(result.data)) {
        dataToUse = result.data;
      } else if (result.items && Array.isArray(result.items)) {
        dataToUse = result.items;
      } else if (typeof result === 'object' && result !== null) {
        dataToUse = Object.values(result).filter(Array.isArray).flat();
      }

      if (dataToUse.length === 0) {
        setError('No menu items found in the response.');
        setShowError(true);
      } else {
        setMenuData(dataToUse);
        setError(null);
        setShowError(false);
      }
    } catch (error) {
      console.error('Error fetching menu:', error);
      setError(error.message || 'Failed to load menu data. Please try again later.');
      setShowError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMenuData();
  }, []);

  // Auto-dismiss error after 10 seconds
  useEffect(() => {
    let errorTimer;
    if (showError) {
      errorTimer = setTimeout(() => {
        setShowError(false);
      }, 10000);
    }
    return () => clearTimeout(errorTimer);
  }, [showError]);

  // Category filters (applied AFTER date validation)
  const getBarItems = (data) =>
    data.filter((item) =>
      item.SchemeTitle?.toLowerCase().match(/vodka|whiskey|whisky|rum|gin|cocktail|drink|beer/)
    );

  const getRestaurantItems = (data) =>
    data.filter((item) =>
      item.SchemeTitle?.toLowerCase().match(/food|meal|dish|menu|special|combo|pizza|burger|pasta/)
    );

  const getHealthClubItems = (data) =>
    data.filter((item) =>
      item.SchemeTitle?.toLowerCase().match(/health|fitness|spa|wellness|gym|workout|massage|yoga/)
    );

  // ✅ First: filter only VALID offers
  const validMenuData = useMemo(() => {
    return menuData.filter(item =>
      isValidItem(item) && isOfferValidToday(item.SchemeFromDate, item.SchemeToDate)
    );
  }, [menuData]);

  // ✅ Then: categorize only valid items
  const barItems = useMemo(() => getBarItems(validMenuData), [validMenuData]);
  const restaurantItems = useMemo(() => getRestaurantItems(validMenuData), [validMenuData]);
  const healthClubItems = useMemo(() => getHealthClubItems(validMenuData), [validMenuData]);

  // ✅ Declare hasAnyOffers BEFORE the useEffect that uses it
  const hasAnyOffers = useMemo(() => {
    return barItems.length > 0 || restaurantItems.length > 0 || healthClubItems.length > 0;
  }, [barItems, restaurantItems, healthClubItems]);

  useEffect(() => {
    let timer;
    if (!loading && !error && !hasAnyOffers) {
      setShowNoOffersMessage(true);
      timer = setTimeout(() => setShowNoOffersMessage(false), 10000);
    } else {
      setShowNoOffersMessage(false);
    }
    return () => clearTimeout(timer);
  }, [loading, error, hasAnyOffers]);

  const handleShowModal = (category) => {
    setShowModal((prev) => ({ ...prev, [category]: true }));
  };

  const handleCloseModal = (category) => {
    setShowModal((prev) => ({ ...prev, [category]: false }));
  };

  // Render modals for each category
  const renderModal = (category, title, items) => (
    <Modal
      key={category}
      show={showModal[category]}
      onHide={() => handleCloseModal(category)}
      size="lg"
      centered
      dialogClassName="modal-fullscreen-custom"
      aria-labelledby={`${category}-menu-modal-title`}
    >
      <Modal.Header closeButton>
        <Modal.Title id={`${category}-menu-modal-title`}>{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading ? (
          <div className="loading">
            <p>Loading menu items...</p>
            <Spinner animation="border" />
          </div>
        ) : error ? (
          <div className="error">
            <p>{error}</p>
            <button className="card-button retry-btn" onClick={fetchMenuData}>
              Retry
            </button>
          </div>
        ) : (
          <div className="table-responsive">
            <table className="table table-striped table-hover">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Valid Till</th>
                  <th>Rate</th>
                  <th>Discount</th>
                  <th>Qty</th>
                  <th>Free Qty</th>
                </tr>
              </thead>
              <tbody>
                {items.length > 0 ? (
                  items.map((item, index) => (
                    <tr key={index}>
                      <td>{item.SchemeTitle}</td>
                      <td>{formatDate(item.SchemeToDate)}</td>
                      <td>{formatCurrency(item.SchemeRate)}</td>
                      <td>{formatDiscount(item.SchemeDisRate)}</td>
                      <td>{item.SchemeQty ?? 'N/A'}</td>
                      <td>{item.SchemeFreeQty ?? 'N/A'}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" className="text-center">
                      No {title.toLowerCase()} items available today
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </Modal.Body>
    </Modal>
  );

  return (
    <div className="dashboard-container">
      {/* Loading */}
      {loading && (
        <div className="loading">
          <p>Fetching offers...</p>
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </div>
      )}

      {/* Error - now auto-dismisses after 10 seconds */}
      {error && showError && (
        <div className="error-message">
          {error}
        </div>
      )}

      {/* Offers Section */}
      {!loading && !error && hasAnyOffers && (
        <>
          <h2 className="offers-title">Offers</h2>
          <div className="offer-buttons">
            {barItems.length > 0 && (
              <button className="offer-button bar-btn" onClick={() => handleShowModal('bar')}>
                Bar
              </button>
            )}
            {restaurantItems.length > 0 && (
              <button className="offer-button restaurant-btn" onClick={() => handleShowModal('restaurant')}>
                Restaurant
              </button>
            )}
            {healthClubItems.length > 0 && (
              <button className="offer-button health-btn" onClick={() => handleShowModal('health')}>
                Health Club & Other
              </button>
            )}
          </div>
        </>
      )}

      {/* Auto-hiding "No Offers" Message */}
      {showNoOffersMessage && (
        <div className="no-offers-message">
          <p>No offers available at the moment.</p>
        </div>
      )}

      {/* Modals */}
      {renderModal('bar', 'Bar Menu', barItems)}
      {renderModal('restaurant', 'Restaurant Menu', restaurantItems)}
      {renderModal('health', 'Health Club & Other', healthClubItems)}
    </div>
  );
};

export default OfferPage;